import { useState, useEffect, useContext } from "react";
import { graphql, Script } from "gatsby";
import styles from "./show-block.module.less";
import UtilityArea from "components/block-elements/utility-area/utility-area";
import { GlobalContext } from "components/stores/global-context";
import AddToCalendar from "components/block-elements/add-to-calendar/add-to-calendar";
import RichTextRenderer from "components/block-elements/rich-text-renderer/rich-text-renderer";
import { openOverlay } from "components/block-elements/overlay-link/overlay-link";
import SiteConfigurationStore from "components/stores/site-configuration-store";
import {
  SessionStorageHelper,
  DateHelper,
  EventAggregator,
  createDate,
  ScheduleStore,
  JetshopStore,
  CountdownBlock,
  SimplifyGatsbyQuery,
  multipleClassNames,
  isNonEmptyArray,
} from "@parksandresorts/core";
import animateScrollTo from "animated-scroll-to";
import siteMetadata from "../../../../site-metadata";

import { mapContentfulFieldsToCoreComponentProps } from "../countdown-block/countdown-block";

type Event = Awaited<ReturnType<typeof ScheduleStore.getEventsForShowId>>[0];

const ShowBlock = ({
  contentful_id,
  preHeading,
  title,
  preamble,
  content,
  countdownBlock,
  reccuringShow,
  location,
  customDateText,
  customDateTime,
  hideEventDateAndOnlyShowCustomText,
  isCancelled,
  addToCalendarImage,
  imageInList,
  disableAddToCalendar,
  ctaButtonLink,
  product,
}: SimplifyGatsbyQuery<Queries.ShowBlockFragment>) => {
  const globalContext = useContext(GlobalContext);

  const [utilityLoading, setUtilityLoading] = useState(true);
  const [structuredData, setStructuredData] = useState<any>(null);
  const [utilityDate, setUtilityDate] = useState<Date | null>(null);

  const productContentfulId = product?.contentful_id;
  const productPageSlug = product?.productPageLink?.slug;

  function getLinkUrl(link?: SimplifyGatsbyQuery<Queries.LinkFragment>) {
    if (!link) return null;
    if (link.internalLink?.slug) return link.internalLink.slug;
    if (link.externalUrl) return link.externalUrl;
    if (link.asset?.file?.url) return link.asset.file.url;
    return null;
  }

  const buttonLink = getLinkUrl(ctaButtonLink);
  const buttonLabel = ctaButtonLink?.title ?? SiteConfigurationStore.getLabel("BUY_NOW", "Buy now");

  useEffect(() => {
    if (contentful_id) {
      SessionStorageHelper.setItem("currentConcertPage", contentful_id);
    }

    const showEventDate = !(hideEventDateAndOnlyShowCustomText && customDateText);

    async function fetchEvents() {
      try {
        const events = await ScheduleStore.getEventsForShowId(contentful_id);

        if (!isNonEmptyArray(events)) {
          setUtilityLoading(false);
          return;
        }

        const eventToUse = events.length === 1 ? events[0] : findNextEvent(events);

        if (eventToUse) {
          setUtilityDate(eventToUse.startDateAndTime);
          SessionStorageHelper.setItem("productDate", eventToUse.startDateAndTime, DateHelper.getTimestamp(5));
          await setupStructuredData(eventToUse);
        }

        setUtilityLoading(false);
      } catch (err) {
        EventAggregator.publish("trackException", {
          exception: err,
          customMessage: "ShowBlock getEventsForShowId error",
        });
        setUtilityLoading(false);
      }
    }

    if (showEventDate && contentful_id) {
      fetchEvents();
    } else {
      setUtilityLoading(false);
    }

    return () => {
      SessionStorageHelper.removeItem("currentConcertPage");
    };
  }, [contentful_id, hideEventDateAndOnlyShowCustomText, customDateText]);

  function findNextEvent(events: Event[]) {
    const today = createDate();
    const sortedUpcomingEvents = events
      .filter((e) => e.startDateAndTime > today)
      .sort((a, b) => (a.startDateAndTime < b.startDateAndTime ? -1 : 1));

    return sortedUpcomingEvents[0] || null;
  }

  async function setupStructuredData(event: Event) {
    const structured: Record<string, any> = {
      "@context": "https://schema.org/",
      "@type": "Event",
      name: title,
      startDate: event.startDateAndTime?.toISOString(),
      endDate: event.endDateAndTime?.toISOString(),
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: isCancelled ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
      location: {
        "@type": "Place",
        name: location,
        address: {
          "@type": "PostalAddress",
          streetAddress: siteMetadata.address.street,
          addressLocality: `${siteMetadata.address.city}, ${siteMetadata.address.country}`,
          postalCode: siteMetadata.address.postalCode,
        },
      },
      image: [imageInList?.file?.url],
      description: preamble?.preamble,
      performer: {
        "@type": "PerformingGroup",
        name: title,
      },
      organizer: {
        "@type": "Organization",
        name: "Gröna Lund",
        url: "https://www.gronalund.com/",
      },
    };

    const extendedProduct = product?.productData?.jetshopId
      ? await JetshopStore.getExtendedProduct(product.productData.jetshopId)
      : null;

    const offers =
      productPageSlug || extendedProduct
        ? {
            "@type": "Offer",
            ...(productPageSlug && {
              url: siteMetadata.siteUrl + productPageSlug,
            }),
            ...(extendedProduct && {
              price: extendedProduct.price,
              priceCurrency: "SEK",
            }),
          }
        : undefined;

    if (offers) {
      structured.offers = offers;
    }

    setStructuredData(structured);
  }

  function onClickCTAButton() {
    if (!productContentfulId || !productPageSlug) return;

    const productElement = document.querySelector(`#id-${productContentfulId}`);
    if (productElement) {
      const overlayContainer = document.querySelector(".page-overlay--active");
      animateScrollTo(productElement, {
        verticalOffset: -64,
        elementToScroll: overlayContainer || undefined,
      });
    } else {
      openOverlay(productPageSlug, globalContext);
    }

    EventAggregator.publish("trackEvent", {
      category: "Concert Page",
      action: "Clicked button",
      label: "Buy now",
    });
  }

  return (
    <section className="block block--with-utility" data-contentful-id={contentful_id}>
      <div className={multipleClassNames("block--max-width", styles.outerWrapper)}>
        <UtilityArea
          recurring={reccuringShow}
          // @ts-expect-error - UtilityArea is not strictly typed
          visible={!utilityLoading}
          location={location}
          customDateText={customDateText}
          customDateTime={customDateTime}
          hideEventDateAndOnlyShowCustomText={hideEventDateAndOnlyShowCustomText}
          nextShowTime={utilityDate}
          isCancelled={isCancelled}
          buttonLabel={isCancelled ? null : buttonLabel}
          buttonLink={buttonLink ? buttonLink : null}
          onClickButton={isCancelled || !productContentfulId || !productPageSlug ? null : onClickCTAButton}
        />

        <div className={multipleClassNames("block--max-width-narrow block--padded", styles.contentWrapper)}>
          {preHeading && <h6 className={styles.intro}>{preHeading}</h6>}
          {title && (
            <h2 className={multipleClassNames(styles.title, !preHeading ? styles.title__extraMarginTop : "")}>
              {title}
            </h2>
          )}

          {preamble?.preamble && (
            <div className={multipleClassNames("preamble", styles.preamble)}>{preamble.preamble}</div>
          )}

          {content && <RichTextRenderer richTextData={content} />}

          {countdownBlock && (
            <CountdownBlock {...mapContentfulFieldsToCoreComponentProps(countdownBlock)} withMargin={false} />
          )}
        </div>

        {utilityDate && !disableAddToCalendar && (
          <div className={multipleClassNames("block--max-width-narrow", styles.contentWrapper)}>
            <AddToCalendar
              showIds={[contentful_id]}
              image={addToCalendarImage?.file?.url || imageInList?.file?.url || ""}
            />
          </div>
        )}
      </div>

      {structuredData && (
        <Script id="show-block-json-ld" type="application/ld+json">
          {JSON.stringify(structuredData, null, "\t")}
        </Script>
      )}
    </section>
  );
};

export default ShowBlock;

export const fragmentShowBlock = graphql`
  fragment ShowBlock on ContentfulShowBlock {
    contentful_id
    node_locale
    id
    sortIndex
    preHeading
    title
    preamble {
      preamble
    }
    content {
      raw
      references {
        __typename
        ...ProductBlock
        ...RichTextFeature
      }
    }
    countdownBlock {
      __typename
      ...CountdownBlock
    }
    label
    labelComponentTheme
    shortDescriptionInList
    pageLink {
      slug
    }
    imageInList {
      file {
        url
      }
    }
    tags {
      title
    }
    length
    location
    customDateText
    customDateTime
    hideEventDateAndOnlyShowCustomText
    product {
      contentful_id
      productData {
        jetshopId
      }
      productPageLink {
        slug
      }
    }
    mapLink {
      ...Link
    }
    addToCalendarImage {
      file {
        url
      }
    }
    disableAddToCalendar
    reccuringShow
    isCancelled
    ctaButtonLink {
      ...Link
    }
  }
`;
